import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import TextButton from '../../sharedComponents/TextButton/TextButton';
import Typography from '../../sharedComponents/Typography/Typography';
import { AddPayoutAccountBaseModuleProps } from './AddPayoutAccountBaseModule.types';
import Button from '../../components/Button/Button';
import styles from './add-payout-account-base-module.module.scss';
import { PayoutProviderDisplay } from '../../utils/types/Entity';

const AddPayoutAccountBaseModule: React.FC<AddPayoutAccountBaseModuleProps> = ({
  step,
  stepTitles,
  provider,
  exit,
  edit,
  save,
  formReady,
  formID,
  processingRequest,
  errorMsg,
  resetErrorMsg,
  children,
  disableSteps,
}): JSX.Element => {
  const { t } = useTranslation();

  type Cfg =
    | {
        label: string;
        disabled: boolean;
        clickHandling: 'submit';
        formID: string;
        textButtonLabel?: string;
        header?: string;
      }
    | {
        label: string;
        disabled: boolean;
        clickHandling: () => void;
        textButtonLabel?: string;
        header?: string;
      };

  const PayPalConfig = {
    0: {
      label: t('action.next'),
      disabled: !formReady,
      clickHandling: 'submit' as const,
      formID,
    },
    1: {
      label: t('action.save_account'),
      disabled: processingRequest,
      clickHandling: save,
      textButtonLabel: t('action.edit'),
    },
  };
  const BankConfig = {
    0: {
      label: t('action.next'),
      disabled: !formReady,
      clickHandling: save,
    },
    1: {
      label: t('action.submit'),
      disabled: !formReady,
      clickHandling: save,
      textButtonLabel: t('action.back_bank_currency'),
      header: t('sunshine.transfer_details.header'),
    },
    2: {
      label: t('action.accept'),
      disabled: !formReady,
      clickHandling: save,
      textButtonLabel: t('action.back_transfer_details'),
      header: t('sunshine.verify_information.header'),
    },
    3: {
      label: t('action.save_account'),
      disabled: !formReady,
      clickHandling: save,
      textButtonLabel: '',
    },
  };

  const buttonConfigurationMap: Record<
    PayoutProviderDisplay,
    Record<number, Cfg>
  > = {
    [PayoutProviderDisplay.PayPal]: PayPalConfig,
    [PayoutProviderDisplay.Bank]: BankConfig,
  };

  const btnConf =
    buttonConfigurationMap[provider as PayoutProviderDisplay][step];

  useEffect(() => {
    if (step === 0) {
      resetErrorMsg();
    }
  }, [step, resetErrorMsg]);

  return (
    <div>
      <Typography variant="body-2" className={styles.header}>
        {step === 0
          ? t('sunshine.label.enter_provider_info', { provider })
          : stepTitles?.[step] ??
            btnConf.header ??
            t('sunshine.label.confirm_provider_info', { provider })}
      </Typography>

      <div className={styles.contentWrapper}>{children}</div>

      <TextButton
        label={`${
          step === 0
            ? t('sunshine.action.add_different_account')
            : btnConf.textButtonLabel
        }`}
        clickHandling={step === 0 ? exit : edit}
        inline
      />

      <div className={styles.buttonWrapper}>
        {!disableSteps && (
          <Button
            colorVariant="ocean"
            label={btnConf.label}
            clickHandling={
              btnConf.clickHandling == 'submit'
                ? 'submit'
                : () => btnConf.clickHandling()
            }
            errorMsg={errorMsg}
            disabled={btnConf.disabled}
            formID={formID}
          />
        )}
      </div>

      <Typography
        variant="body-3"
        align="left"
        className={styles.willBeSavedText}
      >
        {t('sunshine.label.account_info_disclaimer')}
      </Typography>
    </div>
  );
};

export default AddPayoutAccountBaseModule;
